import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BellIcon,
  ClipboardDocumentListIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  UserGroupIcon,
  HeartIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { 
  BellIcon as BellIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
} from '@heroicons/react/24/solid';
import { format, formatDistanceToNow, isToday, isYesterday, isThisWeek } from 'date-fns';
import { toast } from 'react-hot-toast';
import { notificationsApi, type Notification, type CreateNotificationDto } from '../api/notifications';
import CreateNotificationModal from '../components/CreateNotificationModal';
import EditNotificationModal from '../components/EditNotificationModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';

export default function Notifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  
  // Notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationFilter, setNotificationFilter] = useState<'all' | 'unread' | 'breeding' | 'health' | 'task'>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'CRITICAL' | 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW'>('all');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'yesterday' | 'week' | 'month' | 'custom'>('all');

  // Additional modal states for notifications CRUD
  const [showCreateNotification, setShowCreateNotification] = useState(false);
  const [showEditNotification, setShowEditNotification] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingNotification, setDeletingNotification] = useState<Notification | null>(null);
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const handleRealtimeNotification = useCallback((notification: Notification) => {
    setNotifications(prev => {
      const existingIndex = prev.findIndex(n => n.id === notification.id);
      if (existingIndex >= 0) {
        const previous = prev[existingIndex];
        if (previous.status === 'UNREAD' && notification.status !== 'UNREAD') {
          setUnreadCount(count => Math.max(0, count - 1));
        } else if (previous.status !== 'UNREAD' && notification.status === 'UNREAD') {
          setUnreadCount(count => count + 1);
        }
        const updated = [...prev];
        updated[existingIndex] = notification;
        return updated;
      }
      if (notification.status === 'UNREAD') {
        setUnreadCount(count => count + 1);
      }
      return [notification, ...prev];
    });
  }, []);

  const handleRealtimeNotificationDeleted = useCallback((notificationId: string) => {
    setNotifications(prev => {
      const removed = prev.find(n => n.id === notificationId);
      if (removed?.status === 'UNREAD') {
        setUnreadCount(count => Math.max(0, count - 1));
      }
      return prev.filter(n => n.id !== notificationId);
    });
  }, []);

  const handleUrgentNotification = useCallback((notification: Notification) => {
    handleRealtimeNotification(notification);
    toast.error(notification.title, { duration: 6000 });
  }, [handleRealtimeNotification]);

  const { connected: wsConnected } = useWebSocket({
    userId: user?.id,
    autoConnect: !!user?.id,
    onNotificationReceived: handleRealtimeNotification,
    onNotificationDeleted: handleRealtimeNotificationDeleted,
    onUrgentNotification: handleUrgentNotification,
  });

  useEffect(() => {
    loadData();

    if (wsConnected) {
      return;
    }

    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [wsConnected]);

  const loadData = async () => {
    try {
      setLoading(true);
      await loadNotifications();
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const [notificationsResponse, unreadResponse] = await Promise.all([
        notificationsApi.getAll({ limit: 50 }),
        notificationsApi.getUnreadCount(),
      ]);
      setNotifications(notificationsResponse.data);
      setUnreadCount(unreadResponse.data.count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  // Notification handlers
  const handleMarkAsRead = async (notification: Notification) => {
    if (notification.status === 'READ') return;

    try {
      await notificationsApi.markAsRead(notification.id);
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, status: 'READ' as const } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, status: 'READ' as const })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleDismissNotification = async (notification: Notification) => {
    try {
      await notificationsApi.dismiss(notification.id);
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      if (notification.status === 'UNREAD') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      toast.success('Notification dismissed');
    } catch (error) {
      console.error('Error dismissing notification:', error);
      toast.error('Failed to dismiss notification');
    }
  };

  // New CRUD handlers for notifications
  const handleCreateNotification = async (data: CreateNotificationDto) => {
    try {
      const response = await notificationsApi.create(data);
      setNotifications(prev => [response.data, ...prev]);
      setShowCreateNotification(false);
      toast.success('Notification created successfully');
    } catch (error) {
      console.error('Error creating notification:', error);
      toast.error('Failed to create notification');
    }
  };

  const handleEditNotification = (notification: Notification) => {
    setEditingNotification(notification);
    setShowEditNotification(true);
  };

  const handleUpdateNotification = async (id: string, data: Partial<Notification>) => {
    try {
      const response = await notificationsApi.update(id, data);
      setNotifications(prev => 
        prev.map(n => n.id === id ? response.data : n)
      );
      setShowEditNotification(false);
      setEditingNotification(null);
      toast.success('Notification updated successfully');
    } catch (error) {
      console.error('Error updating notification:', error);
      toast.error('Failed to update notification');
    }
  };

  const handleDeleteNotification = (notification: Notification) => {
    setDeletingNotification(notification);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteNotification = async () => {
    if (!deletingNotification) return;

    try {
      await notificationsApi.delete(deletingNotification.id);
      setNotifications(prev => prev.filter(n => n.id !== deletingNotification.id));
      if (deletingNotification.status === 'UNREAD') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setShowDeleteConfirm(false);
      setDeletingNotification(null);
      toast.success('Notification deleted successfully');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  // Bulk operations
  const handleSelectNotification = (notificationId: string) => {
    setSelectedNotifications(prev => {
      if (prev.includes(notificationId)) {
        return prev.filter(id => id !== notificationId);
      }
      return [...prev, notificationId];
    });
  };

  const handleSelectAllNotifications = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n.id));
    }
  };

  const handleBulkMarkAsRead = async () => {
    try {
      await notificationsApi.bulkUpdate(selectedNotifications, { status: 'READ' });
      setNotifications(prev => 
        prev.map(n => 
          selectedNotifications.includes(n.id) 
            ? { ...n, status: 'READ' as const }
            : n
        )
      );
      // Update unread count
      const unreadSelected = notifications.filter(n => 
        selectedNotifications.includes(n.id) && n.status === 'UNREAD'
      ).length;
      setUnreadCount(prev => Math.max(0, prev - unreadSelected));
      setSelectedNotifications([]);
      toast.success(`${selectedNotifications.length} notifications marked as read`);
    } catch (error) {
      console.error('Error bulk marking as read:', error);
      toast.error('Failed to update notifications');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedNotifications.length} notifications? This action cannot be undone.`)) {
      return;
    }

    try {
      await notificationsApi.bulkDelete(selectedNotifications);
      const deletedUnread = notifications.filter(n => 
        selectedNotifications.includes(n.id) && n.status === 'UNREAD'
      ).length;
      setNotifications(prev => 
        prev.filter(n => !selectedNotifications.includes(n.id))
      );
      setUnreadCount(prev => Math.max(0, prev - deletedUnread));
      setSelectedNotifications([]);
      toast.success(`${selectedNotifications.length} notifications deleted`);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Failed to delete notifications');
    }
  };

  // Utility functions
  const getNotificationIcon = (notification: Notification) => {
    switch (notification.type) {
      case 'breeding':
        return <UserGroupIcon className="h-6 w-6" />;
      case 'health':
        return <HeartIcon className="h-6 w-6" />;
      case 'task':
        return <ClipboardDocumentListIcon className="h-6 w-6" />;
      default:
        return <InformationCircleIcon className="h-6 w-6" />;
    }
  };

  const getNotificationColor = (notification: Notification) => {
    if (notification.status === 'READ') {
      return 'text-slate-400 bg-slate-50/80';
    }

    switch (notification.priority) {
      case 'URGENT':
        return 'text-red-600 bg-red-50';
      case 'HIGH':
        return 'text-orange-600 bg-orange-50';
      case 'MEDIUM':
        return 'text-cyan-600 bg-cyan-50';
      default:
        return 'text-slate-600 bg-slate-50/80';
    }
  };

  const getPriorityIcon = (priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT') => {
    switch (priority) {
      case 'URGENT':
        return <ExclamationTriangleIconSolid className="h-4 w-4 text-red-500" />;
      case 'HIGH':
        return <ExclamationTriangleIcon className="h-4 w-4 text-orange-500" />;
      case 'MEDIUM':
        return <InformationCircleIcon className="h-4 w-4 text-blue-500" />;
      default:
        return <InformationCircleIcon className="h-4 w-4 text-slate-500" />;
    }
  };

  // Quick filter function
  const applyQuickFilter = (filterType: string) => {
    switch (filterType) {
      case 'critical':
        setPriorityFilter('CRITICAL');
        setNotificationFilter('all');
        break;
      case 'URGENT':
        setPriorityFilter('URGENT');
        setNotificationFilter('all');
        break;
      case 'today':
        setDateFilter('today');
        break;
      case 'unread':
        setNotificationFilter('unread');
        setPriorityFilter('all');
        break;
    }
  };

  // Enhanced notification filtering
  const isWithinDateRange = (dateString: string): boolean => {
    const date = new Date(dateString);
    const now = new Date();
    
    switch (dateFilter) {
      case 'today':
        return isToday(date);
      case 'yesterday':
        return isYesterday(date);
      case 'week':
        return isThisWeek(date);
      case 'month':
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      default:
        return true;
    }
  };

  // Filter functions
  const filteredNotifications = notifications.filter(notification => {
    // Type filter
    switch (notificationFilter) {
      case 'unread':
        if (notification.status !== 'UNREAD') return false;
        break;
      case 'breeding':
      case 'health':
      case 'task':
        if (notification.type !== notificationFilter) return false;
        break;
      default:
        break;
    }

    // Priority filter
    if (priorityFilter !== 'all' && notification.priority !== priorityFilter) {
      return false;
    }

    // Module filter
    if (moduleFilter !== 'all') {
      const notificationModule = notification.metadata?.moduleType;
      if (notificationModule !== moduleFilter) return false;
    }

    // Date filter
    if (!isWithinDateRange(notification.createdAt)) {
      return false;
    }

    return true;
  });

  // Get main stats
  const getMainStats = () => {
    return [
      {
        name: 'Total Notifications',
        value: notifications.length.toString(),
        icon: BellIcon,
        href: '/notifications',
        change: '',
        changeType: 'neutral' as const,
      },
      {
        name: 'Unread',
        value: unreadCount.toString(),
        icon: BellIconSolid,
        href: '/notifications',
        change: '',
        changeType: unreadCount > 0 ? 'increase' as const : 'neutral' as const,
      },
      {
        name: 'Task Notifications',
        value: notifications.filter(n => n.type === 'task').length.toString(),
        icon: ClipboardDocumentListIcon,
        href: '/notifications',
        change: '',
        changeType: 'neutral' as const,
      },
    ];
  };

  const mainStats = getMainStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <PageHero
        title="Notifications"
        subtitle="Stay updated with important alerts and system notifications"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCreateNotification(true)}
              className="btn btn-primary focus-pop"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Create Notification
            </button>
            {unreadCount > 0 && (
              <button type="button" onClick={handleMarkAllAsRead} className="btn btn-secondary focus-pop">
                <CheckIcon className="h-4 w-4" />
                Mark All Read ({unreadCount})
              </button>
            )}
            <button type="button" onClick={loadData} className="btn btn-secondary focus-pop" aria-label="Refresh">
              <ArrowPathIcon className="h-4 w-4" />
            </button>
          </div>
        }
      />
      
      {/* Main Stats */}
      <div className="mt-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {mainStats.map((item) => (
            <div
              key={item.name}
              className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow hover:shadow-md transition-shadow sm:px-6 sm:pt-6"
            >
              <dt>
                <div className="absolute rounded-md bg-gradient-to-br from-cyan-500 to-violet-500 p-3">
                  <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <p className="ml-16 truncate text-sm font-medium text-slate-500">{item.name}</p>
              </dt>
              <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
                <p className="text-2xl font-semibold text-slate-900">{item.value}</p>
                <p
                  className={`ml-2 flex items-baseline text-sm font-semibold ${
                    item.changeType === 'increase'
                      ? 'text-red-600'
                      : 'text-slate-600'
                  }`}
                >
                  {item.change}
                </p>
              </dd>
            </div>
          ))}
        </div>
      </div>

      {/* Notifications Content */}
      <div className="mt-8">
        {selectedNotifications.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200/70 bg-white/80 px-4 py-3">
            <span className="text-sm text-slate-600">{selectedNotifications.length} selected</span>
            <button type="button" onClick={handleBulkMarkAsRead} className="btn btn-secondary text-sm">
              <CheckIcon className="h-4 w-4" />
              Mark Read
            </button>
            <button type="button" onClick={handleBulkDelete} className="btn btn-secondary text-sm">
              <TrashIcon className="h-4 w-4" />
              Delete
            </button>
          </div>
        )}

        {/* Enhanced Notification Filters */}
        <div className="mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
              <select
                value={notificationFilter}
                onChange={(e) => setNotificationFilter(e.target.value as any)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                <option key="all-1753461297383" value="all">All Types</option>
                <option key="unread-1753461297383" value="unread">Unread Only</option>
                <option key="breeding-1753461297383" value="breeding">Breeding</option>
                <option key="health-1753461297383" value="health">Health</option>
                <option key="task-1753461297383" value="task">Tasks</option>
                <option key="supply_alert-1753461297383" value="supply_alert">Supply Alerts</option>
                <option key="equipment_alert-1753461297383" value="equipment_alert">Equipment</option>
                <option key="disease_alert-1753461297383" value="disease_alert">Disease Alerts</option>
                <option key="financial_alert-1753461297383" value="financial_alert">Financial</option>
                <option key="customer_notification-1753461297383" value="customer_notification">Customer</option>
                <option key="document_expiry-1753461297383" value="document_expiry">Document Expiry</option>
                <option key="system-1753461297383" value="system">System</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as any)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                <option key="all-1753461297383" value="all">All Priorities</option>
                <option key="CRITICAL-1753461297383" value="CRITICAL">Critical</option>
                <option key="URGENT-1753461297383" value="URGENT">Urgent</option>
                <option key="HIGH-1753461297383" value="HIGH">High</option>
                <option key="MEDIUM-1753461297383" value="MEDIUM">Medium</option>
                <option key="LOW-1753461297383" value="LOW">Low</option>
              </select>
            </div>

            {/* Module Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Module</label>
              <select
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                <option key="all-1753461297383" value="all">All Modules</option>
                <option key="breeding-1753461297383" value="breeding">Breeding</option>
                <option key="health-1753461297383" value="health">Health Tests</option>
                <option key="feed-stock-1753461297383" value="feed-stock">Feed Stock</option>
                <option key="equipment-1753461297383" value="equipment">Equipment</option>
                <option key="customer-1753461297383" value="customer">Customer</option>
                <option key="documents-1753461297383" value="documents">Documents</option>
                <option key="accounting-1753461297383" value="accounting">Accounting</option>
                <option key="system-1753461297383" value="system">System</option>
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Date Range</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as any)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
              >
                <option key="all-1753461297383" value="all">All Time</option>
                <option key="today-1753461297383" value="today">Today</option>
                <option key="yesterday-1753461297383" value="yesterday">Yesterday</option>
                <option key="week-1753461297383" value="week">This Week</option>
                <option key="month-1753461297383" value="month">This Month</option>
                <option key="custom-1753461297383" value="custom">Custom Range</option>
              </select>
            </div>
          </div>

          {/* Quick Filter Tags */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm font-medium text-slate-700 mr-2">Quick Filters:</span>
            {[
              { key: 'critical', label: 'Critical', color: 'bg-rose-100 text-rose-800' },
              { key: 'urgent', label: 'Urgent', color: 'bg-orange-100 text-orange-800' },
              { key: 'today', label: 'Today', color: 'bg-cyan-100 text-cyan-800' },
              { key: 'unread', label: 'Unread', color: 'bg-cyan-100 text-cyan-800' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => applyQuickFilter(filter.key)}
                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${filter.color} hover:opacity-80`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white shadow rounded-lg">
          {filteredNotifications.length === 0 ? (
            <div className="p-4">
              <EmptyState
                className="mt-0"
                title="No notifications"
                description={
                  notificationFilter === 'all'
                    ? "You're all caught up!"
                    : `No ${notificationFilter} notifications found.`
                }
                icon={<BellIcon className="h-6 w-6" />}
                action={
                  notificationFilter === 'all' ? (
                    <button
                      type="button"
                      onClick={() => setShowCreateNotification(true)}
                      className="btn btn-primary focus-pop"
                    >
                      <PlusIcon className="h-5 w-5" />
                      Create Notification
                    </button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <>
              {/* Bulk Actions Header */}
              <div className="px-4 py-3 border-b border-gray-200 bg-slate-50/80">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.length === filteredNotifications.length && filteredNotifications.length > 0}
                    onChange={handleSelectAllNotifications}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-slate-600">
                    {selectedNotifications.length === 0 
                      ? 'Select all' 
                      : `${selectedNotifications.length} of ${filteredNotifications.length} selected`
                    }
                  </span>
                </div>
              </div>
              
              <div className="divide-y divide-gray-200">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-slate-50/80 ${
                      notification.status === 'UNREAD' ? 'bg-cyan-50' : ''
                    } ${selectedNotifications.includes(notification.id) ? 'bg-cyan-50' : ''}`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedNotifications.includes(notification.id)}
                        onChange={() => handleSelectNotification(notification.id)}
                        className="mt-1 h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                      />
                      
                      {/* Notification Icon */}
                      <div className={`flex-shrink-0 p-2 rounded-full ${getNotificationColor(notification)}`}>
                        {getNotificationIcon(notification)}
                      </div>
                      
                      {/* Notification Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className={`text-sm font-medium ${
                            notification.status === 'UNREAD' ? 'text-slate-900' : 'text-slate-600'
                          }`}>
                            {notification.title}
                          </p>
                          {getPriorityIcon(notification.priority as any)}
                        </div>
                        <p className={`text-sm mt-1 ${
                          notification.status === 'UNREAD' ? 'text-slate-700' : 'text-slate-500'
                        }`}>
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-slate-400">
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                          </p>
                          <div className="flex items-center space-x-2">
                            {/* Edit Button */}
                            <button
                              onClick={() => handleEditNotification(notification)}
                              className="text-xs text-cyan-600 hover:text-cyan-800"
                              title="Edit notification"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            
                            {/* Mark as Read Button */}
                            {notification.status === 'UNREAD' && (
                              <button
                                onClick={() => handleMarkAsRead(notification)}
                                className="text-xs text-cyan-600 hover:text-cyan-800"
                                title="Mark as read"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                            )}
                            
                            {/* Delete Button */}
                            <button
                              onClick={() => handleDeleteNotification(notification)}
                              className="text-xs text-red-600 hover:text-red-800"
                              title="Delete notification"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                            
                            {/* Dismiss Button */}
                            <button
                              onClick={() => handleDismissNotification(notification)}
                              className="text-xs text-slate-400 hover:text-slate-600"
                              title="Dismiss"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Notification Modal */}
      <CreateNotificationModal
        isOpen={showCreateNotification}
        onClose={() => setShowCreateNotification(false)}
        onSubmit={handleCreateNotification}
      />

      {/* Edit Notification Modal */}
      <EditNotificationModal
        isOpen={showEditNotification}
        onClose={() => {
          setShowEditNotification(false);
          setEditingNotification(null);
        }}
        notification={editingNotification}
        onSubmit={handleUpdateNotification}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingNotification(null);
        }}
        onConfirm={confirmDeleteNotification}
        title="Delete Notification"
        message={`Are you sure you want to delete "${deletingNotification?.title}"? This action cannot be undone.`}
      />
    </div>
  );
} 
